import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, VIEW } from "const"
import {
    StateType,
    hasAccess,
    hasProjectAccess,
    hasLifecycleAccess,
    replaceHistoryState,
} from "client/utils"
import {
    PRM_LIFECYCLE,
    PRM_LIFECYCLE_PERMISSIONS,
    PRM_LIFECYCLE_PHASE_LIST,
    StoreType,
    LifecycleProps,

    LoadLifecycleById,
    LoadLifecyclePermissions,
    IsOwnerLifecycle,
    IsResponsibleLifecycle,

    setLifecycleView,
    setLifecyclePath,
    setLifecyclePhaseActive,
    setLifecyclePhaseList,
    setLifecycleConnectionActive,
    setLifecycleConnectionList,
    setSystemLifecyclePermissions,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    Loader,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import NotFound from "client/parts/notFound"
import Form from "./form"
import Permissions from "../permissions"
import Model from "../model"


const DETAILS = "#details"
const PERMISSIONS = "#permissions"
const MODEL = "#model"

const menu = [DETAILS, PERMISSIONS, MODEL]

const View = ({
    lifecycleId = null,
    hash = null,
}: LifecycleProps) => {
    const baseRoute = useSelector((state: StoreType) => state.lifecycle.baseRoute)
    const lifecycle = useSelector((state: StoreType) => state.lifecycle.lifecycleView)
    const reloadView = useSelector((state: StoreType) => state.lifecycle.reloadView)

    const project = useSelector((state: StoreType) => state.project.projectView)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState(menu.includes(hash) ? hash : DETAILS)
    const [renderModel, setRenderModel] = useState(hash === MODEL)

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => (
        // clear lifecycle on unmount
        () => {
            dispatch(setSystemLifecyclePermissions(null))
            dispatch(setLifecyclePath([]))

            dispatch(setLifecyclePhaseList([]))
            dispatch(setLifecycleConnectionList([]))

            dispatch(setLifecyclePhaseActive(null))
            dispatch(setLifecycleConnectionActive(null))
        }
    ), [])

    useEffect(() => {
        if (!reloadView) return
        setLoadState(PENDING)
        Promise.all([
            LoadLifecycleById(lifecycleId, userData.accountId, project.id).then((res) => {
                dispatch(setLifecycleView(res))
            }),
            LoadLifecyclePermissions(lifecycleId).then((res) => {
                dispatch(setSystemLifecyclePermissions(res.permissions))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadView])

    const handleMenuClick = (tab: string) => {
        setActiveTab(tab)
        if (tab === MODEL) setRenderModel(true)
        replaceHistoryState(`${baseRoute}/${VIEW}/${lifecycleId}${tab}`)
    }


    const viewAccess = !project.id
        ? hasAccess(PRM_LIFECYCLE, VIEW)
        : hasProjectAccess(PRM_LIFECYCLE, VIEW)

    const lifecycleViewAccess = hasLifecycleAccess(PRM_LIFECYCLE, VIEW)
    const permissionsViewAccess = hasLifecycleAccess(PRM_LIFECYCLE_PERMISSIONS, VIEW)
    const phaseListViewAccess = hasLifecycleAccess(PRM_LIFECYCLE_PHASE_LIST, VIEW)


    const isOwner = IsOwnerLifecycle(project.id, lifecycle, userData)
    const isResponsible = IsResponsibleLifecycle(lifecycle, userData)

    const allowedToViewDetails = viewAccess || isOwner || isResponsible || lifecycleViewAccess
    const allowedToViewPermissions = viewAccess || isOwner || permissionsViewAccess
    const allowedToViewPhaseList = viewAccess || isOwner || isResponsible || phaseListViewAccess


    if (!allowedToViewDetails) return <AccessDenied />
    if (!lifecycle) return <NotFound />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{`${lang.viewLifecycle} # ${lifecycle.id || ""}`}</PageTitle>
            </PageHeader>
            <PageContent>
                <PageMenu>
                    <PageMenuButton
                        isActive={activeTab === DETAILS}
                        onClick={() => handleMenuClick(DETAILS)}
                    >
                        {lang.details}
                    </PageMenuButton>
                    {
                        allowedToViewPermissions && userData.accountId !== 1 && (
                            <PageMenuButton
                                isActive={activeTab === PERMISSIONS}
                                onClick={() => handleMenuClick(PERMISSIONS)}
                            >
                                {lang.permissions}
                            </PageMenuButton>
                        )
                    }
                    {
                        allowedToViewPhaseList && (
                            <PageMenuButton
                                isActive={activeTab === MODEL}
                                onClick={() => handleMenuClick(MODEL)}
                            >
                                {lang.model}
                            </PageMenuButton>
                        )
                    }
                </PageMenu>


                <Tab show={activeTab === DETAILS}>
                    {
                        loadState === PENDING
                        && <Loader />
                    }
                    <Form />
                </Tab>

                <Tab show={activeTab === PERMISSIONS}>
                    {
                        allowedToViewPermissions && userData.accountId !== 1
                        && <Permissions />
                    }
                    {
                        !allowedToViewPermissions
                        && <AccessDenied tabMode />
                    }
                </Tab>

                <Tab show={activeTab === MODEL}>
                    {
                        allowedToViewPhaseList && renderModel && lifecycle && lifecycle.id
                        && <Model />
                    }
                    {
                        !allowedToViewPhaseList
                        && <AccessDenied tabMode />
                    }
                </Tab>
            </PageContent>
        </Container>
    )
}
export default View
